import packageJson from '@/../package.json'
import * as Constants from '@/lib/constants'
import storage from '@/lib/storage'

export const CONNECT_ENV_PROD = 100
export const CONNECT_ENV_LOCAL = -100
export const CONNECT_ENV_DEV = 0
export const CONNECT_ENV_DEVFIRE = -300

/**
 * ApplicationConfig
 */
const ApplicationConfig = {
  // アプリのバージョン
  VERSION: packageJson.version,

  // 環境設定
  CURRENT_ENV: CONNECT_ENV_PROD,

  // naviサーバーが1リージョン統合したか？
  // 本番環境で、かつ、この値がtrueの場合
  // naviサーバーのエンドポイントはオハイオ固定になります
  NAVI_SERVER_UNIFIED: true,

  // Realtime機能が利用可能か
  CONNECT_REALTIME_ENABLED: false,

  // Group機能が利用可能か
  CONNECT_GROUP_ENABLED: true,

  // Crop機能が利用可能か
  CROP_ENABLED: true,

  // 天気APIをOpenMeteoから取得するか
  OPEN_METEO_ENABLED: true,

  // 可変施肥 (VRA) マップ機能 (beta)。本番でも有効 (2026-08 リリース)
  isVraMapEnabled: function() {
    return true
  },

  // 高低差マップ機能 (beta)。本番でも有効
  isElevationMapEnabled: function() {
    return true
  },

  getCurrentEnvironmentName: function() {
    switch (ApplicationConfig.CURRENT_ENV) {
      case CONNECT_ENV_LOCAL:
        return Constants.CONNECT_ENV_LOCAL_NAME
      case CONNECT_ENV_DEV:
        return Constants.CONNECT_ENV_DEV_NAME
      case CONNECT_ENV_PROD:
        return Constants.CONNECT_ENV_PROD_NAME
      case CONNECT_ENV_DEVFIRE:
        return Constants.CONNECT_ENV_DEVFIRE_NAME
      default:
        throw new Error('Invalid ApplicationEnvironment: ' + ApplicationConfig.CURRENT_ENV)
    }
  },

  // Server
  getAuthServer: function() {
    switch (ApplicationConfig.CURRENT_ENV) {
      case CONNECT_ENV_LOCAL:
        return Constants.AUTH_SERVER_LOCAL
      case CONNECT_ENV_DEV:
        return Constants.AUTH_SERVER_DEV
      case CONNECT_ENV_PROD:
        return Constants.AUTH_SERVER_PROD
      default:
        throw new Error('Invalid auth server for ApplicationEnvironment: ' + ApplicationConfig.CURRENT_ENV)
    }
  },

  getPayServer: function() {
    switch (ApplicationConfig.CURRENT_ENV) {
      case CONNECT_ENV_LOCAL:
        return Constants.PAY_SERVER_LOCAL
      case CONNECT_ENV_DEV:
        return Constants.PAY_SERVER_DEV
      case CONNECT_ENV_PROD:
        return Constants.PAY_SERVER_PROD
      default:
        throw new Error('Invalid pay server for ApplicationEnvironment: ' + ApplicationConfig.CURRENT_ENV)
    }
  },

  getManagerServer: function() {
    switch (ApplicationConfig.CURRENT_ENV) {
      case CONNECT_ENV_LOCAL:
        return Constants.MANAGER_SERVER_LOCAL
      case CONNECT_ENV_DEV:
      case CONNECT_ENV_DEVFIRE:
        return Constants.MANAGER_SERVER_DEV
      case CONNECT_ENV_PROD:
        return Constants.MANAGER_SERVER_PROD
      default:
        throw new Error('Invalid manager server for ApplicationEnvironment: ' + ApplicationConfig.CURRENT_ENV)
    }
  },
  getNaviServer: function() {
    let userInfo = storage.getUserInfo()
    switch (ApplicationConfig.CURRENT_ENV) {
      case CONNECT_ENV_LOCAL:
        return Constants.NAVI_SERVER_LOCAL
      case CONNECT_ENV_DEV:
        return Constants.NAVI_SERVER_DEV
      case CONNECT_ENV_PROD:
        if (userInfo) {
          if (ApplicationConfig.NAVI_SERVER_UNIFIED) {
            return 'https://navi-us-east-2.agribus-connect.net'
          }
          let region = userInfo.region
          if (region) {
            return Constants.NAVI_SERVER_PROD.replace(Constants.SERVER_URL_REGION_MARK, region)
          }
        }
        throw new Error('Failed generate navi server url for invalid userInfo: ' + userInfo)
      // return "https://navi-test.agribus-connect.net"
      case CONNECT_ENV_DEVFIRE:
        return Constants.NAVI_SERVER_DEVFIRE
      default:
        throw new Error('Invalid navi server for ApplicationEnvironment: ' + ApplicationConfig.CURRENT_ENV)
    }
  },
  getEnterpriseServer: function() {
    switch (ApplicationConfig.CURRENT_ENV) {
      case CONNECT_ENV_LOCAL:
        return Constants.ENTERPRISE_SERVER_LOCAL
      case CONNECT_ENV_DEV:
      case CONNECT_ENV_DEVFIRE:
        return Constants.ENTERPRISE_SERVER_DEV
      case CONNECT_ENV_PROD:
        return Constants.ENTERPRISE_SERVER_PROD
      default:
        throw new Error('Invalid enterprise server for ApplicationEnvironment: ' + ApplicationConfig.CURRENT_ENV)
    }
  },
  getGrowServer: function() {
    switch (ApplicationConfig.CURRENT_ENV) {
      case CONNECT_ENV_LOCAL:
        return Constants.GROW_SERVER_LOCAL
      case CONNECT_ENV_DEV:
      case CONNECT_ENV_DEVFIRE:
        return Constants.GROW_SERVER_DEV
      case CONNECT_ENV_PROD:
        return Constants.GROW_SERVER_PROD
      default:
        throw new Error('Invalid grow server for ApplicationEnvironment: ' + ApplicationConfig.CURRENT_ENV)
    }
  },
  getDatastoreServer: function() {
    switch (ApplicationConfig.CURRENT_ENV) {
      case CONNECT_ENV_LOCAL:
        return Constants.DATASTORE_SERVER_LOCAL
      case CONNECT_ENV_DEV:
      case CONNECT_ENV_DEVFIRE:
        return Constants.DATASTORE_SERVER_DEV
      case CONNECT_ENV_PROD:
        return Constants.DATASTORE_SERVER_PROD
      default:
        throw new Error('Invalid datastore server for ApplicationEnvironment: ' + ApplicationConfig.CURRENT_ENV)
    }
  },

  refreshTokenIntervalSeconds: Constants.REFRESH_TOKEN_INTERVAL,

  isProductionEnv: function() {
    return ApplicationConfig.CURRENT_ENV === CONNECT_ENV_PROD
  },

  getGtagTrackingId: function() {
    if (ApplicationConfig.isProductionEnv()) {
      return Constants.GTAG_TRACKING_ID.production
    } else {
      return Constants.GTAG_TRACKING_ID.development
    }
  },

  getUserPlan: function() {
    return storage.getPurchaseInfo()
  },

  getUserLangConsideringDefault: function() {
    return storage.getUserLangConsideringDefault()
  },

  getUserDatePickerFormat() {
    const lang = storage.getUserLangConsideringDefault()
    if (lang === Constants.LANG_DE) {
      return Constants.DATE_PICKER_FORMAT_DE
    } else if (lang === Constants.LANG_ES) {
      return Constants.DATE_PICKER_FORMAT_ES
    } else if (lang === Constants.LANG_JA) {
      return Constants.DATE_PICKER_FORMAT_JA
    } else if (lang === Constants.LANG_PT) {
      return Constants.DATE_PICKER_FORMAT_PT
    } else if (lang === Constants.LANG_PL) {
      return Constants.DATE_PICKER_FORMAT_PL
    } else if (lang === Constants.LANG_RU) {
      return Constants.DATE_PICKER_FORMAT_RU
    } else if (lang === Constants.LANG_TH) {
      return Constants.DATE_PICKER_FORMAT_TH
    }
    return Constants.DATE_PICKER_FORMAT_EN
  },

  getUserDateMomentFormat() {
    const lang = storage.getUserLangConsideringDefault()
    if (lang === Constants.LANG_DE) {
      return Constants.DATE_MOMENT_FORMAT_DE
    } else if (lang === Constants.LANG_ES) {
      return Constants.DATE_MOMENT_FORMAT_ES
    } else if (lang === Constants.LANG_JA) {
      return Constants.DATE_MOMENT_FORMAT_JA
    } else if (lang === Constants.LANG_PT) {
      return Constants.DATE_MOMENT_FORMAT_PT
    } else if (lang === Constants.LANG_RU) {
      return Constants.DATE_MOMENT_FORMAT_RU
    } else if (lang === Constants.LANG_TH) {
      return Constants.DATE_MOMENT_FORMAT_TH
    }
    return Constants.DATE_MOMENT_FORMAT_EN
  },

  getUserMonthMomentFormat() {
    const lang = storage.getUserLangConsideringDefault()
    if (lang === Constants.LANG_DE) {
      return Constants.DATE_MOMENT_MONTH_FORMAT_DE
    } else if (lang === Constants.LANG_ES) {
      return Constants.DATE_MOMENT_MONTH_FORMAT_ES
    } else if (lang === Constants.LANG_JA) {
      return Constants.DATE_MOMENT_MONTH_FORMAT_JA
    } else if (lang === Constants.LANG_PT) {
      return Constants.DATE_MOMENT_MONTH_FORMAT_PT
    } else if (lang === Constants.LANG_RU) {
      return Constants.DATE_MOMENT_MONTH_FORMAT_RU
    } else if (lang === Constants.LANG_TH) {
      return Constants.DATE_MOMENT_MONTH_FORMAT_TH
    }
    return Constants.DATE_MOMENT_MONTH_FORMAT_EN
  },

  // FIXME
  getDefaultUser: function() {
    return {
      email: null,
      password: null,
    }
  },

  getClientId: function() {
    switch (ApplicationConfig.CURRENT_ENV) {
      case CONNECT_ENV_LOCAL:
      case CONNECT_ENV_DEV:
        return 'connectweb'
      case CONNECT_ENV_PROD:
        return 'app-yeqrxrkw-716110664377'
      default:
        throw new Error('Invalid ApplicationEnvironment: ' + ApplicationConfig.CURRENT_ENV)
    }
  },

  getClientSecret: function() {
    switch (ApplicationConfig.CURRENT_ENV) {
      case CONNECT_ENV_LOCAL:
      case CONNECT_ENV_DEV:
        return 'connectweb-secret'
      case CONNECT_ENV_PROD:
        return 'BD1TOCWbxdGHnPtmmZaFmkZ7RtkLWhTdu2V4vn9BYMTh4B7kd4CQUoxmJKsQabzsbeikx5TnPxHiocrgLibZChza3AYKM82208tDdjgala7G5Um15BUEeuNhuEkNC0AE'
      default:
        throw new Error('Invalid ApplicationEnvironment: ' + ApplicationConfig.CURRENT_ENV)
    }
  },
  getTaskFilterServer: function() {
    switch (ApplicationConfig.CURRENT_ENV) {
      case CONNECT_ENV_LOCAL:
      case CONNECT_ENV_DEV:
        return Constants.TASK_FILTER_ENDPOINT_DEV
      case CONNECT_ENV_PROD:
        return Constants.TASK_FILTER_ENDPOINT_PROD
      default:
        throw new Error('Invalid taskfilter server for ApplicationEnvironment: ' + ApplicationConfig.CURRENT_ENV)
    }
  },
  getCognitoBaseUrl: function() {
    switch (ApplicationConfig.CURRENT_ENV) {
      case CONNECT_ENV_LOCAL:
        return (
          'https://' +
          Constants.GOOGLE_OAUTH_DOMAIN_LOCAL +
          '.auth.' +
          Constants.GOOGLE_OAUTH_RESION_LOCAL +
          '.amazoncognito.com'
        )

      case CONNECT_ENV_DEV:
        return (
          'https://' +
          Constants.GOOGLE_OAUTH_DOMAIN_DEV +
          '.auth.' +
          Constants.GOOGLE_OAUTH_RESION_DEV +
          '.amazoncognito.com'
        )
      case CONNECT_ENV_PROD:
        return 'https://' + Constants.GOOGLE_OAUTH_DOMAIN_PROD
      default:
        throw new Error('Invalid cognito for ApplicationEnvironment: ' + ApplicationConfig.CURRENT_ENV)
    }
  },
  getGoogleOAuthRedirectUrl: function() {
    switch (ApplicationConfig.CURRENT_ENV) {
      case CONNECT_ENV_LOCAL:
        return Constants.GOOGLE_OAUTH_REDIRECT_URL_LOCAL

      case CONNECT_ENV_DEV:
        return Constants.GOOGLE_OAUTH_REDIRECT_URL_DEV

      case CONNECT_ENV_PROD:
        return Constants.GOOGLE_OAUTH_REDIRECT_URL_PROD

      default:
        throw new Error('Invalid cognito for ApplicationEnvironment: ' + ApplicationConfig.CURRENT_ENV)
    }
  },
  getGoogleOAuthRedirectForSSOUrl: function() {
    switch (ApplicationConfig.CURRENT_ENV) {
      case CONNECT_ENV_LOCAL:
        return Constants.GOOGLE_OAUTH_REDIRECT_URL_LOCAL

      case CONNECT_ENV_DEV:
        return Constants.GOOGLE_OAUTH_REDIRECT_SSO_URL_DEV

      case CONNECT_ENV_PROD:
        return Constants.GOOGLE_OAUTH_REDIRECT_SSO_URL_PROD

      default:
        throw new Error('Invalid cognito for ApplicationEnvironment: ' + ApplicationConfig.CURRENT_ENV)
    }
  },
  getCongitoClientId: function() {
    switch (ApplicationConfig.CURRENT_ENV) {
      case CONNECT_ENV_LOCAL:
        return Constants.COGNITO_CLIENT_ID_LOCAL

      case CONNECT_ENV_DEV:
        return Constants.COGNITO_CLIENT_ID_DEV

      case CONNECT_ENV_PROD:
        return Constants.COGNITO_CLIENT_ID_PROD

      default:
        throw new Error('Invalid cognito for ApplicationEnvironment: ' + ApplicationConfig.CURRENT_ENV)
    }
  },
  getGrowApiKey: function() {
    switch (ApplicationConfig.CURRENT_ENV) {
      case CONNECT_ENV_LOCAL:
        return Constants.GROW_API_KEY_LOCAL
      case CONNECT_ENV_DEV:
        return Constants.GROW_API_KEY_DEV
      case CONNECT_ENV_PROD:
        return Constants.GROW_API_KEY_PROD
      default:
        throw new Error('No ApiKey for current env')
    }
  },
  getFireTestCompareApiKey() {
    switch (ApplicationConfig.CURRENT_ENV) {
      case CONNECT_ENV_LOCAL:
        return Constants.FireTestApiKey.Compare.dev
      case CONNECT_ENV_DEV:
        return Constants.FireTestApiKey.Compare.dev
      case CONNECT_ENV_PROD:
        return Constants.FireTestApiKey.Compare.prod
      default:
        throw new Error('No ApiKey for current env')
    }
  },
  getFireTestDataCopyApiKey() {
    switch (ApplicationConfig.CURRENT_ENV) {
      case CONNECT_ENV_LOCAL:
        return Constants.FireTestApiKey.Data.local
      case CONNECT_ENV_DEV:
        return Constants.FireTestApiKey.Data.dev
      case CONNECT_ENV_PROD:
        return Constants.FireTestApiKey.Data.prod
      default:
        throw new Error('No ApiKey for current env')
    }
  },
}

export default ApplicationConfig
